"use client";

import React from "react";
import { motion } from "framer-motion";
import { Box} from "@mui/material";
import { AnimatedButton } from "./AnimatedButton";

// Define gradient animation variants
const grad1 = {
  initial: { x1: "0%", x2: "0%", y1: "80%", y2: "100%" },
  animate: {
    x1: ["0%", "0%", "200%"],
    x2: ["0%", "0%", "180%"],
    y1: ["80%", "0%", "0%"],
    y2: ["100%", "20%", "20%"],
  },
};

const grad2 = {
  initial: { x1: "0%", x2: "0%", y1: "80%", y2: "100%" },
  animate: {
    x1: ["20%", "100%", "100%"],
    x2: ["0%", "90%", "90%"],
    y1: ["80%", "80%", "-20%"],
    y2: ["100%", "100%", "0%"],
  },
};

const grad3 = {
  initial: { x1: "0%", x2: "0%", y1: "80%", y2: "100%" },
  animate: {
    x1: ["20%", "100%", "100%"],
    x2: ["0%", "90%", "90%"],
    y1: ["80%", "80%", "-20%"],
    y2: ["100%", "100%", "0%"],
  },
};

const grad4 = {
  initial: { x1: "0%", x2: "0%", y1: "160%", y2: "180%" },
  animate: { x1: "0%", x2: "10%", y1: "-40%", y2: "-20%" },
};

const grad5 = {
  initial: { x1: "-40%", x2: "-10%", y1: "0%", y2: "20%" },
  animate: {
    x1: ["40%", "0%", "0%"],
    x2: ["10%", "0%", "0%"],
    y1: ["0%", "0%", "180%"],
    y2: ["20%", "20%", "200%"],
  },
};

// Component for the animated SVG
const SVGs: React.FC = () => {
  return (
    <motion.svg
      width="900"
      height="434"
      viewBox="0 0 900 434"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M269 220.5H16.5C10.9772 220.5 6.5 224.977 6.5 230.5V398.5"
        stroke="#1e293b"
      />
      <path
        d="M568 200H841C846.523 200 851 195.523 851 190V40"
        stroke="#1e293b"
      />
      <path
        d="M425.5 274V333C425.5 338.523 421.023 343 415.5 343H152C146.477 343 142 347.477 142 353V426.5"
        stroke="#1e293b"
      />
      <path
        d="M493 274V333.226C493 338.749 497.477 343.226 503 343.226H760C765.523 343.226 770 347.703 770 353.226V427"
        stroke="#1e293b"
      />
      <path d="M380 168V17C380 11.4772 384.477 7 390 7H414" stroke="#1e293b" />

      <motion.path
        d="M269 220.5H16.5C10.9772 220.5 6.5 224.977 6.5 230.5V398.5"
        stroke="url(#grad1)"
      />
      <motion.path
        d="M568 200H841C846.523 200 851 195.523 851 190V40"
        stroke="url(#grad2)"
      />
      <motion.path
        d="M425.5 274V333C425.5 338.523 421.023 343 415.5 343H152C146.477 343 142 347.477 142 353V426.5"
        stroke="url(#grad3)"
      />
      <motion.path
        d="M493 274V333.226C493 338.749 497.477 343.226 503 343.226H760C765.523 343.226 770 347.703 770 353.226V427"
        stroke="url(#grad4)"
      />
      <motion.path
        d="M380 168V17C380 11.4772 384.477 7 390 7H414"
        stroke="url(#grad5)"
      />

      <defs>
        <motion.linearGradient
          id="grad1"
          variants={grad1}
          initial="initial"
          animate="animate"
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <GradientColors />
        </motion.linearGradient>
        <motion.linearGradient
          id="grad2"
          variants={grad2}
          initial="initial"
          animate="animate"
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <GradientColors />
        </motion.linearGradient>
        <motion.linearGradient
          id="grad3"
          variants={grad3}
          initial="initial"
          animate="animate"
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <GradientColors />
        </motion.linearGradient>
        <motion.linearGradient
          id="grad4"
          variants={grad4}
          initial="initial"
          animate="animate"
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <GradientColors />
        </motion.linearGradient>
        <motion.linearGradient
          id="grad5"
          variants={grad5}
          initial="initial"
          animate="animate"
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <GradientColors />
        </motion.linearGradient>
      </defs>
      {/* Github Icon  */}
      <motion.svg
        width="70px"
        height="70px"
        viewBox="0 0 73 73"
        x="404.5"
        y="-28"
        fill="#6FC8FF"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="lightToDark" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6FC8FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#004B87" stopOpacity="1" />
          </linearGradient>
        </defs>
        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <g
            id="container"
            transform="translate(2.000000, 2.000000)"
            fillRule="nonzero"
          >
            <rect
              id="mask"
              stroke="#1e293b"
              strokeWidth="2"
              fill="url(#lightToDark)"
              x="-1"
              y="-1"
              width="71"
              height="71"
              rx="14"
            ></rect>
            <path
              d="M58.3067362,21.4281798 C55.895743,17.2972267 52.6253846,14.0267453 48.4948004,11.615998 C44.3636013,9.20512774 39.8535636,8 34.9614901,8 C30.0700314,8 25.5585181,9.20549662 21.4281798,11.615998 C17.2972267,14.0266224 14.0269912,17.2972267 11.615998,21.4281798 C9.20537366,25.5590099 8,30.0699084 8,34.9607523 C8,40.8357654 9.71405782,46.1187277 13.1430342,50.8109917 C16.5716416,55.5036246 21.0008949,58.7507436 26.4304251,60.5527176 C27.0624378,60.6700211 27.5302994,60.5875152 27.8345016,60.3072901 C28.1388268,60.0266961 28.290805,59.6752774 28.290805,59.2545094 C28.290805,59.1842994 28.2847799,58.5526556 28.2730988,57.3588401 C28.2610487,56.1650247 28.2553926,55.1235563 28.2553926,54.2349267 L27.4479164,54.3746089 C26.9330843,54.468919 26.2836113,54.5088809 25.4994975,54.4975686 C24.7157525,54.4866252 23.9021284,54.4044881 23.0597317,54.2517722 C22.2169661,54.1004088 21.4330982,53.749359 20.7075131,53.1993604 C19.982297,52.6493618 19.4674649,51.9294329 19.1631397,51.0406804 L18.8120898,50.2328353 C18.5780976,49.6950097 18.2097104,49.0975487 17.7064365,48.4426655 C17.2031625,47.7871675 16.6942324,47.3427912 16.1794003,47.108799 L15.9336039,46.9328437 C15.7698216,46.815909 15.6178435,46.6748743 15.4773006,46.511215 C15.3368806,46.3475556 15.2317501,46.1837734 15.1615401,46.0197452 C15.0912072,45.855594 15.1494901,45.7209532 15.3370036,45.6153308 C15.5245171,45.5097084 15.8633939,45.4584343 16.3551097,45.4584343 L17.0569635,45.5633189 C17.5250709,45.6571371 18.104088,45.9373622 18.7947525,46.4057156 C19.4850481,46.8737001 20.052507,47.4821045 20.4972521,48.230683 C21.0358155,49.1905062 21.6846737,49.9218703 22.4456711,50.4251443 C23.2060537,50.9284182 23.9727072,51.1796248 24.744894,51.1796248 C25.5170807,51.1796248 26.1840139,51.121096 26.7459396,51.0046532 C27.3072505,50.8875956 27.8338868,50.7116403 28.3256025,50.477771 C28.5362325,48.9090515 29.1097164,47.7039238 30.0455624,46.8615271 C28.7116959,46.721353 27.5124702,46.5102313 26.4472706,46.2295144 C25.3826858,45.9484285 24.2825656,45.4922482 23.1476478,44.8597436 C22.0121153,44.2280998 21.0701212,43.44374 20.3214198,42.5080169 C19.5725954,41.571802 18.9580429,40.3426971 18.4786232,38.821809 C17.9989575,37.300306 17.7590632,35.5451796 17.7590632,33.5559381 C17.7590632,30.7235621 18.6837199,28.3133066 20.5326645,26.3238191 C19.6665366,24.1944035 19.7483048,21.8072644 20.778215,19.1626478 C21.4569523,18.951772 22.4635002,19.1100211 23.7973667,19.6364115 C25.1314792,20.1630477 26.1082708,20.6141868 26.7287253,20.9882301 C27.3491798,21.3621504 27.8463057,21.6790175 28.2208409,21.9360032 C30.3978419,21.3277217 32.644438,21.0235195 34.9612442,21.0235195 C37.2780503,21.0235195 39.5251383,21.3277217 41.7022622,21.9360032 L43.0362517,21.0938524 C43.9484895,20.5319267 45.0257392,20.0169716 46.2654186,19.5488642 C47.5058357,19.0810026 48.4543466,18.9521409 49.1099676,19.1630167 C50.1627483,21.8077563 50.2565666,24.1947724 49.3901927,26.324188 C51.2390143,28.3136755 52.1640399,30.7245457 52.1640399,33.556307 C52.1640399,35.5455485 51.9232849,37.3062081 51.444357,38.8393922 C50.9648143,40.3728223 50.3449746,41.6006975 49.5845919,42.5256002 C48.8233486,43.4503799 47.8753296,44.2285916 46.7404118,44.8601125 C45.6052481,45.4921252 44.504759,45.9483056 43.4401742,46.2293914 C42.3750975,46.5104772 41.1758719,46.7217219 39.8420054,46.8621419 C41.0585683,47.9149226 41.6669728,49.5767225 41.6669728,51.846804 L41.6669728,59.2535257 C41.6669728,59.6742937 41.8132948,60.0255895 42.1061847,60.3063064 C42.3987058,60.5865315 42.8606653,60.6690374 43.492678,60.5516109 C48.922946,58.7498829 53.3521992,55.5026409 56.7806837,50.810008 C60.2087994,46.117744 61.923472,40.8347817 61.923472,34.9597686 C61.9222424,30.0695396 60.7162539,25.5590099 58.3067362,21.4281798 Z"
              id="Shape"
              fill="#FFFFFF"
            />
          </g>
        </g>
      </motion.svg>

      {/* Gmail Icon  */}
      <motion.svg
        width="70px"
        height="70px"
        viewBox="-8 -8 48.00 48.00"
        x="822"
        y=".5"
        fill="#6FC8FF"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="lightToDark" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6FC8FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#004B87" stopOpacity="1" />
          </linearGradient>
        </defs>

        <g id="SVGRepo_bgCarrier">
          <rect
            x="-8"
            y="-8"
            width="48.00"
            height="48.00"
            rx="4.8"
            fill="url(#lightToDark)"
          />
        </g>

        <g id="SVGRepo_iconCarrier">
          <path
            d="M30.996 7.824v17.381c0 0 0 0 0 0.001 0 1.129-0.915 2.044-2.044 2.044-0 0-0 0-0.001 0h-4.772v-11.587l-8.179 6.136-8.179-6.136v11.588h-4.772c0 0 0 0-0 0-1.129 0-2.044-0.915-2.044-2.044 0-0 0-0.001 0-0.001v0-17.381c0-0 0-0.001 0-0.001 0-1.694 1.373-3.067 3.067-3.067 0.694 0 1.334 0.231 1.848 0.619l-0.008-0.006 10.088 7.567 10.088-7.567c0.506-0.383 1.146-0.613 1.84-0.613 1.694 0 3.067 1.373 3.067 3.067v0z"
            fill="#ffffff"
          />
        </g>
      </motion.svg>

      {/* Linkedin Icon  */}
      <motion.svg
        width="70px"
        height="70px"
        viewBox="-5 -5 30.00 30.00"
        x="744"
        y="411"
        fill="#6FC8FF"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="lightToDark" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6FC8FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#004B87" stopOpacity="1" />
          </linearGradient>
        </defs>

        <g id="SVGRepo_bgCarrier" strokeWidth="0">
          <rect
            x="-4.4"
            y="-4.4"
            width="28.80"
            height="28.80"
            rx="3.4560000000000004"
            fill="url(#lightToDark)"
          />
        </g>
        <g
          id="Dribbble-Light-Preview"
          transform="translate(-180.000000, -7479.000000)"
          fill="#ffffff"
        >
          <g id="icons" transform="translate(56.000000, 160.000000)">
            <path
              d="M144,7339 L140,7339 L140,7332.001 C140,7330.081 139.153,7329.01 137.634,7329.01 C135.981,7329.01 135,7330.126 135,7332.001 L135,7339 L131,7339 L131,7326 L135,7326 L135,7327.462 C135,7327.462 136.255,7325.26 139.083,7325.26 C141.912,7325.26 144,7326.986 144,7330.558 L144,7339 L144,7339 Z M126.442,7323.921 C125.093,7323.921 124,7322.819 124,7321.46 C124,7320.102 125.093,7319 126.442,7319 C127.79,7319 128.883,7320.102 128.883,7321.46 C128.884,7322.819 127.79,7323.921 126.442,7323.921 L126.442,7323.921 Z M124,7339 L129,7339 L129,7326 L124,7326 L124,7339 Z"
              id="linkedin-[#ffffff]"
            />
          </g>
        </g>
      </motion.svg>

      {/* fiverr Icon  */}
      <motion.svg
        width="70px"
        height="70px"
         viewBox="-7.5 -7 34 34"
        x="110"
        y="420"
        fill="#6FC8FF"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="lightToDark" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6FC8FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#004B87" stopOpacity="1" />
          </linearGradient>
        </defs>

        <g id="SVGRepo_bgCarrier">
          <rect
            x="-7.5"
            y="-7"
            width="34.00"
            height="34.00"
            rx="4.8"
            fill="url(#lightToDark)"
          />
        </g>
        <g id="SVGRepo_iconCarrier">
          <path
            fill="#ffffff"
            d="M16.25 16.25v-10h-10v-.625c0-1.034.841-1.875 1.875-1.875H10V0H8.125A5.632 5.632 0 0 0 2.5 5.625v.625H0V10h2.5v6.25H0V20h8.75v-3.75h-2.5V10h6.285v6.25H10V20h8.75v-3.75h-2.5z"
          />
        </g>
      </motion.svg>

      {/* Upwork Icon  */}
      <motion.svg
        width="70px"
        height="70px"
        viewBox="-8 -8 48.00 48.00"
        x="0"
        y="378.5"
        fill="#6FC8FF"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="lightToDark" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6FC8FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#004B87" stopOpacity="1" />
          </linearGradient>
        </defs>

        <g id="SVGRepo_bgCarrier">
          <rect
            x="-8"
            y="-8"
            width="48.00"
            height="48.00"
            rx="4.8"
            fill="url(#lightToDark)"
          />
        </g>
        <g id="SVGRepo_iconCarrier">
          <path
            fill="#ffffff"
            d="M24.75 17.542c-1.469 0-2.849-0.62-4.099-1.635l0.302-1.432 0.010-0.057c0.276-1.521 1.13-4.078 3.786-4.078 1.99 0 3.604 1.615 3.604 3.604 0 1.984-1.615 3.599-3.604 3.599zM24.75 6.693c-3.385 0-6.016 2.198-7.083 5.818-1.625-2.443-2.865-5.38-3.583-7.854h-3.646v9.484c-0.005 1.875-1.521 3.391-3.396 3.396-1.875-0.005-3.391-1.526-3.396-3.396v-9.484h-3.646v9.484c0 3.885 3.161 7.068 7.042 7.068 3.885 0 7.042-3.182 7.042-7.068v-1.589c0.708 1.474 1.578 2.974 2.635 4.297l-2.234 10.495h3.729l1.62-7.615c1.417 0.906 3.047 1.479 4.917 1.479 4 0 7.25-3.271 7.25-7.266 0-4-3.25-7.25-7.25-7.25z"
          />
        </g>
      </motion.svg>
    </motion.svg>
  );
};

// Gradient colors component
const GradientColors: React.FC = () => (
  <>
    <stop offset="0" stopColor="rgb(135, 206, 235)" stopOpacity="0" />
    <stop offset="0.325" stopColor="rgb(0, 123, 167)" />
    <stop offset="1" stopColor="#C0C0C0" stopOpacity="0" />
  </>
);

const ContactPulseBeams: React.FC = () => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100%"
      position="relative"
    >
      <AnimatedButton
          component={"a"}
          href="#contact"
          backgroundColor="#0d0d0d"
          textColor="#b8def5"
          gradientColor="rgb(70, 130, 180)"
          glowStart="rgb(135, 206, 235)"
          glowEnd="rgb(0, 123, 167)"
          sx={{fontSize: 24, fontWeight: "bold", height: "4rem", width: "14rem"}}
        >
          Contact Me
        </AnimatedButton>
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <SVGs />
      </Box>
    </Box>
  );
};

export default ContactPulseBeams;
